import { useEffect, useRef, useState } from "react";
import { IoChevronDown, IoCloseCircleOutline } from "react-icons/io5";
import { useAudioEffects } from "../../../hooks/use-audio-effects";
import clickSound from "../../../assets/audio/click.wav";

export interface Option {
  label: string;
  value: string;
  showIndicator?: boolean;
}

interface ArrestOptionsProps {
  label: string;
  options: Option[];
  value: string | string[] | null;
  onChange: (value: string | string[] | null) => void;
  multiple?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

const ArrestOptions = ({
  label,
  options,
  value,
  onChange,
  multiple = false,
  placeholder = "Select...",
  disabled = false,
}: ArrestOptionsProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);
  const { playEffect } = useAudioEffects();
  const inputRef = useRef<any>(null);
  const selectedArray: string[] = multiple
    ? Array.isArray(value)
      ? value
      : []
    : typeof value === "string"
    ? [value]
    : [];

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);

        setSearch("");
      }
    };
    window.addEventListener("click", onDocClick);
    return () => window.removeEventListener("click", onDocClick);
  }, []);

  const toggleOption = (optVal: string) => {
    if (multiple) {
      const current = Array.isArray(value) ? [...value] : [];
      const idx = current.indexOf(optVal);

      if (idx === -1) current.push(optVal);
      else current.splice(idx, 1);

      onChange(current.length ? current : null);
    } else {
      onChange(optVal);
    }
  };

  const clearAll = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    onChange(null);
    setOpen(false);
    setSearch("");
  };

  const filteredOptions = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="w-full" ref={rootRef}>
      <p className="text-[13px] text-[#535353] mb-1">{label}</p>

      <div
        className={`relative border rounded-md bg-gradient-to-r from-[#e4e2e4] to-[#e8e8e8] border-[#989898]/50 min-h-[42px] px-3 py-2 flex items-center justify-between cursor-pointer ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
        onClick={() => {
          if (!disabled) {
            setOpen((s) => !s);
            inputRef.current?.focus();
            playEffect(clickSound);
          }
        }}
      >
        <div className="flex-1 flex flex-wrap gap-2 items-center">
          {selectedArray.length ? (
            selectedArray.map((val) => {
              const opt = options.find((o) => o.value === val);
              return (
                <span
                  key={val}
                  className="text-[13px] bg-[#f2f2f2] px-2 py-1 rounded-full flex items-center gap-1 font-sans"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (multiple) toggleOption(val);
                  }}
                >
                  <span className="pr-1">{opt?.label ?? val}</span>
                  {multiple && (
                    <IoCloseCircleOutline
                      className="w-4 h-4 hover:text-red-500 transition"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleOption(val);
                      }}
                    />
                  )}
                </span>
              );
            })
          ) : (
            <span className="text-[14px] text-[#9b9b9b]">{placeholder}</span>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {selectedArray.length ? (
            <IoCloseCircleOutline
              className="w-5 h-5 text-[#9b9b9b] hover:text-red-500 transition"
              onClick={(e) => clearAll(e)}
            />
          ) : null}

          <IoChevronDown
            className={`w-5 h-5 text-[#666] transition ${
              open ? "rotate-180" : ""
            }`}
          />
        </div>

        <div
          className={`absolute left-0 top-full mt-2 w-full bg-white shadow-lg rounded-md max-h-64 overflow-auto p-2 ${
            open ? "z-50" : "z-[-200] opacity-0"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* 🔍 Search */}
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="w-full mb-2 px-3 py-2 border rounded-md text-[14px] outline-none font-sans"
          />

          <ul className="max-h-56 overflow-auto">
            {filteredOptions.length ? (
              filteredOptions.map((opt) => {
                const checked = selectedArray.includes(opt.value);
                return (
                  <li
                    key={opt.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOption(opt.value);
                      playEffect(clickSound);
                      if (!multiple) setOpen(false);
                    }}
                    className={`px-3 py-2 cursor-pointer hover:bg-gray-100 flex items-center justify-between font-sans ${
                      checked ? "bg-gray-100" : ""
                    }`}
                  >
                    <span className="text-[14px]">
                      {opt.label}{" "}
                      {opt.showIndicator && (
                        <span className="p-[1px] bg-[#bdbbbd]/60 text-black/70 font-sans  px-[3px] text-[11px]">{`[C]`}</span>
                      )}
                    </span>
                    {checked && (
                      <span className="text-[13px] text-[#666]">✓</span>
                    )}
                  </li>
                );
              })
            ) : (
              <p className="text-center text-[13px] text-gray-500 py-3">
                No results
              </p>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ArrestOptions;
